<template>
  <div class="case-scene">
    <div class="image-container">
      <scene-image
        :avatars="avatars"
        :location="caseConf[location]"
        :avatar-key="caseConf._avatar.key"
        :outfit="caseConf.outfit"
        :npc-avatar-key="npc ? npc.avatarKey : null"
        :npc-outfit="npc ? npc.outfit : null"
      />
    </div>
    <div class="tabs-container">
      <vrp-tabs
        :tabs="tabs"
        :active-tab-key="state.activeTabKey"
        :resizeable="false"
        @change-tab="setTabKey"
      >
        <template #item="{ key }">
          <div v-if="key === 'SCENES'" class="scroll-content md-scrollbar md-theme-default">
            <environment-grid
              :environments="environments"
              :selected-environment="caseConf[location].key"
              @select="selectEnvironment"
            />
          </div>
          <div v-if="key === 'PATIENTS'" class="scroll-content md-scrollbar md-theme-default">
            <character-grid
              :avatars="avatars"
              :selected-avatar-key="caseConf._avatar.key"
              :disabled-avatar-keys="disabledForPatient.map(info => info.avatarKey)"
              @select="selectAvatar"
            />
            <character-fields
              :avatars="avatars"
              :voices="voices"
              :name="caseConf.patientName"
              :avatar-key="caseConf._avatar.key"
              :voice-key="caseConf.voiceKey"
              :disabled-voice-keys="disabledForPatient.map(info => info.voiceKey)"
              :user="user"
              :outfit="caseConf.outfit"
              :readonly="readonly"
              @selectName="selectName"
              @selectVoice="selectVoice"
              @selectOutfit="selectOutfit"
            />
            <eye-tracking
              :attention-info="caseConf.attentionInfo"
              :npc="npc"
              :readonly="readonly"
              :update-case-conf="updateCaseConf"
              :patient-label="patientLabel"
            />
          </div>
          <div v-if="key === 'NPCS'" class="scroll-content md-scrollbar md-theme-default">
            <character-grid
              :avatars="avatars"
              :selected-avatar-key="npc ? npc.avatarKey : null"
              allow-unselected
              :disabled-avatar-keys="disabledAvatarKeysForNpc"
              @select="selectNpcAvatar"
            />
            <character-fields
              v-if="npc"
              :avatars="avatars"
              :voices="voices"
              :name="npc.name"
              name-label="NPC Name"
              :avatar-key="npc.avatarKey"
              :voice-key="npc.voiceKey"
              :disabled-voice-keys="disabledVoiceKeysForNpc"
              :user="user"
              :outfit="npc.outfit"
              :show-on-at-start="false"
              :readonly="npcFieldsDisabled"
              @selectName="debouncedSelectNpcName"
              @selectVoice="selectNpcVoice"
              @selectOutfit="selectNpcOutfit"
            />
            <eye-tracking
              v-if="npc"
              :attention-info="caseConf.attentionInfo || {}"
              :npc="npc"
              :is-npc="true"
              :readonly="npcFieldsDisabled"
              :update-case-conf="updateCaseConf"
              :npc-label="npcLabel"
              :patient-label="patientLabel"
            />
          </div>
        </template>
      </vrp-tabs>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { npcForScene, createDefaultDialogue, createDefaultRole } from '~lib/common/utils/npc';
import VrpTabs from '../../common/VrpTabs/VrpTabs.vue';
import SceneImage from '../../common/SceneImage.vue';
import EnvironmentGrid from './EnvironmentGrid.vue';
import CharacterGrid from './CharacterGrid.vue';
import CharacterFields from './CharacterFields.vue';
import EyeTracking from './EyeTracking.vue';
import { npcLabel as getNpcLabel, patientLabel as getPatientLabel } from '~lib/common/utils/labels';

export default {
  name: 'case-scene-builder',
  components: {
    VrpTabs,
    EnvironmentGrid,
    CharacterGrid,
    CharacterFields,
    SceneImage,
    EyeTracking,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    location: {
      type: String,
      default: '_startLocation',
    },
    usedInfo: {
      type: Array,
      default: () => []
    },
    user: {
      type: Object,
      default: () => {}
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    avatars: {
      type: Array,
      default: () => []
    },
    environments: {
      type: Array,
      default: () => []
    },
    voices: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      tabs: this.location === '_startLocation' ? [
        { key: 'SCENES', label: 'Scene', icon: require('@/assets/icons/scene.svg') },
        { key: 'PATIENTS', label: 'Patient', icon: require('@/assets/icons/patient.svg') },
        { key: 'NPCS', label: 'NPC', icon: require('@/assets/icons/npc.svg') },
      ] : [
        { key: 'SCENES', label: 'Scene', icon: require('@/assets/icons/scene.svg') },
        { key: 'NPCS', label: 'NPC', icon: require('@/assets/icons/npc.svg') },
      ],
      state: {
        activeTabKey: 'SCENES',
        updating: false,
      }
    };
  },
  computed: {
    npc() {
      return npcForScene(this.caseConf.npcs, this.location);
    },
    disabledForPatient() {
      return this.usedInfo.filter(info => info.key !== 'PATIENT');
    },
    disabledAvatarKeysForNpc() {
      return this.usedInfo.filter(info => info.key === 'PATIENT').map(info => info.avatarKey);
    },
    disabledVoiceKeysForNpc() {
      return this.usedInfo.filter(info => info.key !== this.location).map(info => info.voiceKey);
    },
    npcFieldsDisabled() {
      const activeScenes = this.npc.activeScenes || [];
      const isInBothScenes = activeScenes.includes('_startLocation') && activeScenes.includes('_endLocation');
      return this.readonly || (isInBothScenes && this.location === '_endLocation');
    },
    patientLabel() {
      return getPatientLabel(this.caseConf, true);
    },
    npcLabel() {
      return getNpcLabel(this.npc, true);
    }
  },
  methods: {
    selectEnvironment(environment) {
      if (this.readonly) return;
      this.$emit('update', { [this.location]: environment });
    },
    selectAvatar(avatar) {
      if (this.state.updating || this.readonly) return;
      const isMilitary = (this.user.productType === 'MILITARY');
      const defaultVoice = this.voices.find(voice => voice.avatarKey === avatar.key && voice.isDefault);
      const voiceKey = defaultVoice ? defaultVoice.id : null;
      const outfit = {
        clothingType: isMilitary ? 'MILITARY_OUTFIT_1': avatar.avatarOptions.defaultOutfit,
        isDirty: false,
        onAtStart: 'CLOTHES_ON',
      };
      this.state.updating = true;
      this.$emit('update', {
        _avatar: avatar,
        voiceKey,
        outfit,
      });
      this.$nextTick(() => {
        this.state.updating = false;
      })
    },
    selectNpcAvatar(avatar) {
      if (this.state.updating || this.readonly) return;
      const newAvatarKey = avatar.key;

      // If selected avatar isn't in the list of npcs, then create a new npc object
      let newNpc = null;
      const existingNpc = this.caseConf.npcs.find(npc => npc.avatarKey === newAvatarKey);
      if (!existingNpc && newAvatarKey) {
        const isMilitary = (this.user.productType === 'MILITARY');
        const defaultVoice = this.voices.find(voice => voice.avatarKey === avatar.key && voice.isDefault);
        const voiceKey = defaultVoice ? defaultVoice.id : null;
        const outfit = {
          clothingType: isMilitary ? 'MILITARY_OUTFIT_1': avatar.avatarOptions.defaultOutfit,
          isDirty: false,
          onAtStart: 'CLOTHES_ON',
        };
        newNpc = {
          avatarKey: avatar.key,
          voiceKey: voiceKey,
          outfit: outfit,
          activeScenes: [ this.location ],
        }
        this.caseConf.npcs.push(newNpc);
      }
      let startAvatarKey;
      let endAvatarKey;
      if (this.location === '_startLocation') {
        startAvatarKey = newAvatarKey;
        const endingNpc = this.caseConf.npcs.find(npc => npc.activeScenes.includes('_endLocation'));
        endAvatarKey = endingNpc ? endingNpc.avatarKey : null;
      } else {
        const startingNpc = this.caseConf.npcs.find(npc => npc.activeScenes.includes('_startLocation'));
        startAvatarKey = startingNpc ? startingNpc.avatarKey : null;
        endAvatarKey = newAvatarKey;
      }

      // Update each npc's activeScenes.  Keep npcs that are no longer selected to preserve their dialogue
      for (const npc of this.caseConf.npcs) {
        if (npc.avatarKey === startAvatarKey) {
          npc.key = '_startLocation';
          npc.activeScenes = [ '_startLocation' ];
          if (npc.avatarKey === endAvatarKey) {
            npc.activeScenes.push('_endLocation');
          }
        } else if (npc.avatarKey === endAvatarKey) {
          npc.key = '_endLocation';
          npc.activeScenes = [ '_endLocation' ];
        } else {
          npc.key = 'none';
          npc.activeScenes = [];
        }
      }

      // When creating a new NPC, give some default values to avoid immediate validation errors
      if (newNpc) {
        newNpc.dialogue = createDefaultDialogue(newNpc);
        newNpc.role = createDefaultRole(newNpc);
      }

      // Do the update
      this.state.updating = true;
      this.$emit('update', {
        npcs: this.caseConf.npcs
      });
      this.$nextTick(() => {
        this.state.updating = false;
      })
    },
    selectName(name) {
      if (this.state.updating || this.readonly) return;
      this.state.updating = true;
      if (!this.caseConf.patientName) {
        this.$set(this.caseConf, 'patientName', name);
      }
      this.$emit('update', {
        patientName: name,
      });
      this.$nextTick(() => {
        this.state.updating = false;
      });
    },
    selectNpcName(name) {
      if (this.state.updating || this.readonly) return;
      this.state.updating = true;
      this.npc.name = name;
      this.$emit('update', {
        npcs: this.caseConf.npcs
      });
      this.$nextTick(() => {
        this.state.updating = false;
      });
    },
    debouncedSelectNpcName: debounce(function(value) {
      this.selectNpcName(value);
    }, 2000),
    selectVoice(voice) {
      if (this.state.updating || this.readonly) return;
      this.state.updating = true;
      this.$emit('update', {
        voiceKey: voice,
      });
      this.$nextTick(() => {
        this.state.updating = false;
      });
    },
    selectNpcVoice(voice) {
      if (this.state.updating || this.readonly) return;
      this.state.updating = true;
      this.npc.voiceKey = voice;
      this.$emit('update', {
        npcs: this.caseConf.npcs
      });
      this.$nextTick(() => {
        this.state.updating = false;
      });
    },
    selectOutfit(outfit) {
      if (this.state.updating || this.readonly) return;
      this.state.updating = true;
      this.$emit('update', {
        outfit,
      });
      this.$nextTick(() => {
        this.state.updating = false;
      });
    },
    selectNpcOutfit(outfit) {
      if (this.state.updating || this.readonly) return;
      this.state.updating = true;
      this.npc.outfit = outfit;
      this.$emit('update', {
        npcs: this.caseConf.npcs
      });
      this.$nextTick(() => {
        this.state.updating = false;
      });
    },
    setTabKey(key) {
      this.state.activeTabKey = key;
    },
    updateCaseConf(fieldName, data, npc = false) {
      if (this.state.updating || this.readonly) return;
      this.state.updating = true;
      if (npc) {
        this.npc[fieldName] = data;
        this.$emit('update', {
          npcs: this.caseConf.npcs
        });
      }
      else {
        this.$emit('update', {
          [fieldName]: data,
        });
      }
      this.$nextTick(() => {
        this.state.updating = false;
      });
    },
  },
}
</script>

<style scoped>
.case-scene {
  margin: 10px 20px 20px 20px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 500px;
}

.image-container {
  width: auto;
  display: flex;
}

@media (max-width: 1530px) {
  .image-container {
    width: 500px;
  }
}

.tabs-container {
  padding-left: 20px;
  flex: 1;
  align-self: stretch;
}

.scroll-content {
  padding-left: 4px;
  padding-top: 10px;
  height: 462px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-wrap: wrap;
  gap: 20px; /* automatically spaces between items, but no gap on the outer edge */
}
</style>